import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  Clock,
  Download,
  FileCheck,
  Github,
  MessageSquare,
  Paperclip,
  Pencil,
  Search,
  Users,
  X,
} from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Skeleton, SkeletonLines } from '@/components/ui/Skeleton';
import { Textarea } from '@/components/ui/Textarea';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { FilePreviewModal } from '@/components/ui/FilePreviewModal';
import { downloadPrivateFile } from '@/lib/download';
import { ApiRequestError } from '@/lib/apiClient';
import { cn, getInitials } from '@/lib/utils';
import type { BadgeTone } from '@/components/ui/Badge';
import type { HomeworkStudentRow } from '@/types/homework';
import { homeworkApi } from '../api/homework.api';
import { HomeworkMeta } from '../components/homework/HomeworkMeta';
import { formatInstituteDateTime } from '../utils';
import { getAvatarColor } from '../utils/avatarColor';

/**
 * Marking one piece of work, child by child.
 *
 * Drawn inside the teacher console layout shell so the navigation rail and
 * header identity are accessible without leaving the grading flow.
 *
 * The whole roster is listed: children who have handed in work, those whose
 * submissions are graded, and those who have not submitted yet. A teacher
 * can record marks for physical paper submissions directly from this view.
 */

type Filter = 'ALL' | 'SUBMITTED' | 'GRADED' | 'PENDING';

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'ALL', label: 'Everyone' },
  { value: 'SUBMITTED', label: 'Handed in, not marked' },
  { value: 'GRADED', label: 'Marked' },
  { value: 'PENDING', label: 'Not handed in' },
];

/** The three states, read off the two fields that define them. */
function stateOf(student: HomeworkStudentRow): Exclude<Filter, 'ALL'> {
  if (student.marksObtained !== null) return 'GRADED';
  return student.submittedAt === null ? 'PENDING' : 'SUBMITTED';
}

/**
 * Visual styling rules for each submission state.
 */
const STATE_STYLE: Record<
  Exclude<Filter, 'ALL'>,
  { label: string; tone: BadgeTone; card: string; avatarBorder: string }
> = {
  GRADED: {
    label: 'Marked',
    tone: 'success',
    card: 'border-ok/30 bg-surface hover:border-ok/50',
    avatarBorder: 'ring-1 ring-ok/30',
  },
  SUBMITTED: {
    label: 'Handed in',
    tone: 'info',
    card: 'border-info/40 bg-surface hover:border-info/60',
    avatarBorder: 'ring-1 ring-info/30',
  },
  PENDING: {
    label: 'Not handed in',
    tone: 'warning',
    card: 'border-dashed border-line-strong bg-surface-2/30',
    avatarBorder: 'ring-1 ring-line',
  },
};

export function TeacherHomeworkReviewPage() {
  const { homeworkId } = useParams<{ homeworkId: string }>();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('ALL');

  const { data, isLoading, error, refetch } = useApiQuery(
    () => homeworkApi.review(homeworkId ?? ''),
    [homeworkId],
  );

  const students = data?.students ?? [];

  /**
   * The tiles' and the filter's counts, read with the same `stateOf` the list
   * filters by. The server's summary counts a child marked from paper work as
   * not handed in, while the list files them under marked — so a tile showing
   * the server's number never matched the list it opened.
   */
  const counts = useMemo(() => {
    const byState: Record<Exclude<Filter, 'ALL'>, number> = { GRADED: 0, SUBMITTED: 0, PENDING: 0 };
    for (const student of students) byState[stateOf(student)] += 1;
    return byState;
  }, [students]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return students.filter((student) => {
      if (filter !== 'ALL' && stateOf(student) !== filter) return false;
      if (!needle) return true;

      return (
        student.fullName.toLowerCase().includes(needle) ||
        (student.rollNumber ?? '').toLowerCase().includes(needle) ||
        student.admissionNumber.toLowerCase().includes(needle)
      );
    });
  }, [students, search, filter]);

  async function downloadWorksheet() {
    if (!data?.homework.attachment?.fileName) return;

    try {
      await downloadPrivateFile(
        homeworkApi.worksheetPath(data.homework.uuid),
        data.homework.attachment.fileName,
      );
    } catch {
      toast.error('Could not download worksheet file. Please try again.');
    }
  }

  return (
    <div className="flex flex-col gap-5 w-full max-w-5xl mx-auto">
      {/* --- Top Navigation & Breadcrumbs ----------------------------------- */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Breadcrumb
          items={[
            { label: 'Homework', to: '/teacher/homework' },
            { label: data?.homework.title ?? 'Review Submissions' },
          ]}
        />

        <Button
          variant="secondary"
          size="sm"
          leftIcon={<ArrowLeft className="h-4 w-4" />}
          onClick={() => navigate('/teacher/homework')}
        >
          Back to assignments
        </Button>
      </div>

      {/* --- Loading State -------------------------------------------------- */}
      {isLoading && (
        <div className="flex flex-col gap-4">
          <div className="card rounded-[16px] p-6 shadow-2xs">
            <SkeletonLines count={4} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Skeleton className="h-20 rounded-[14px]" />
            <Skeleton className="h-20 rounded-[14px]" />
            <Skeleton className="h-20 rounded-[14px]" />
          </div>
          <div className="card rounded-[14px] p-4 shadow-2xs">
            <Skeleton className="h-10 w-full rounded-field" />
          </div>
          <div className="card rounded-[14px] p-5 shadow-2xs">
            <SkeletonLines count={6} />
          </div>
        </div>
      )}

      {/* --- Error State ---------------------------------------------------- */}
      {!isLoading && error && (
        <div className="card rounded-[16px] flex flex-col items-start gap-4 p-6 shadow-2xs">
          <Alert tone="critical" title="Could not load this assignment">
            {error.message}
          </Alert>
          <Button variant="secondary" size="sm" onClick={refetch}>
            Try again
          </Button>
        </div>
      )}

      {/* --- Loaded Content ------------------------------------------------- */}
      {!isLoading && !error && data && (
        <>
          {/* --- 1. Assignment Hero Card -------------------------------------- */}
          <div className="card rounded-[16px] border border-line bg-surface p-5 sm:p-6 shadow-2xs flex flex-col gap-3.5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-pill border border-line bg-surface-2 px-2.5 py-0.5 text-xs font-bold text-ink">
                  {data.homework.displayName}
                </span>
                <span className="rounded-pill border border-accent/20 bg-accent/10 px-2.5 py-0.5 text-xs font-bold text-accent">
                  {data.homework.subject.name}
                </span>
              </div>

              {data.homework.attachment && (
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Download className="h-4 w-4" />}
                  onClick={() => void downloadWorksheet()}
                  title={`Download worksheet ${data.homework.attachment.fileName}`}
                >
                  <span className="max-w-[14rem] truncate">
                    {data.homework.attachment.fileName}
                  </span>
                </Button>
              )}
            </div>

            <div>
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-ink">
                {data.homework.title}
              </h1>
              <p className="mt-1.5 whitespace-pre-line text-sm leading-relaxed text-ink-2">
                {data.homework.instructions}
              </p>
            </div>

            <HomeworkMeta
              homework={data.homework}
              today={data.today}
              className="border-t border-line pt-3"
            />

            {data.homework.maxMarks === null && (
              <SetMaxMarks homeworkUuid={data.homework.uuid} onSaved={refetch} />
            )}
          </div>

          {/* --- 2. Interactive Metric Summary Cards -------------------------- */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
            {/* Card: Marked */}
            <button
              type="button"
              onClick={() => setFilter((prev) => (prev === 'GRADED' ? 'ALL' : 'GRADED'))}
              className={cn(
                'card flex items-center gap-3.5 rounded-[14px] p-3.5 text-left transition-all hover:shadow-xs active:scale-[0.99] border cursor-pointer',
                filter === 'GRADED'
                  ? 'border-ok ring-2 ring-ok/20 bg-ok/5'
                  : 'border-line bg-surface hover:border-ok/40',
              )}
            >
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-ok/15 text-ok border border-ok/25">
                <Check className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold uppercase tracking-wider text-ink-3">Marked</p>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-2xl font-bold text-ok">{counts.GRADED}</span>
                  <span className="text-xs text-ink-3">evaluated</span>
                </div>
              </div>
            </button>

            {/* Card: Handed In */}
            <button
              type="button"
              onClick={() => setFilter((prev) => (prev === 'SUBMITTED' ? 'ALL' : 'SUBMITTED'))}
              className={cn(
                'card flex items-center gap-3.5 rounded-[14px] p-3.5 text-left transition-all hover:shadow-xs active:scale-[0.99] border cursor-pointer',
                filter === 'SUBMITTED'
                  ? 'border-info ring-2 ring-info/20 bg-info/5'
                  : 'border-line bg-surface hover:border-info/40',
              )}
            >
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-info/15 text-info border border-info/25">
                <FileCheck className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold uppercase tracking-wider text-ink-3">Handed In</p>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-2xl font-bold text-info">{counts.SUBMITTED}</span>
                  <span className="text-xs text-ink-3">to mark</span>
                </div>
              </div>
            </button>

            {/* Card: Not Handed In */}
            <button
              type="button"
              onClick={() => setFilter((prev) => (prev === 'PENDING' ? 'ALL' : 'PENDING'))}
              className={cn(
                'card flex items-center gap-3.5 rounded-[14px] p-3.5 text-left transition-all hover:shadow-xs active:scale-[0.99] border cursor-pointer',
                filter === 'PENDING'
                  ? 'border-warn ring-2 ring-warn/20 bg-warn/5'
                  : 'border-line bg-surface hover:border-warn/40',
              )}
            >
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-warn/15 text-warn border border-warn/25">
                <Clock className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold uppercase tracking-wider text-ink-3">Not Handed In</p>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-2xl font-bold text-warn">{counts.PENDING}</span>
                  <span className="text-xs text-ink-3">pending</span>
                </div>
              </div>
            </button>
          </div>

          {/* --- 3. Search & Filter Bar --------------------------------------- */}
          <div className="card flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-[14px] border border-line bg-surface p-3.5 shadow-2xs">
            <div className="flex-1 min-w-0 relative">
              <Input
                label="Search roster"
                hideLabel
                leftIcon={<Search className="h-4 w-4" />}
                placeholder="Search by student name, roll number, or admission number..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="h-10 text-sm"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  aria-label="Clear search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-3 hover:text-ink transition-colors p-1"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-3 shrink-0">
              <Select
                label="Filter status"
                hideLabel
                className="sm:w-56 h-10 text-sm"
                options={FILTERS.map((item) => ({
                  value: item.value,
                  label: `${item.label} (${
                    item.value === 'ALL' ? students.length : counts[item.value]
                  })`,
                }))}
                value={filter}
                onChange={(event) => setFilter(event.target.value as Filter)}
              />

              <span className="hidden sm:inline-block font-mono text-xs font-semibold text-ink-3">
                {visible.length} of {students.length}
              </span>
            </div>
          </div>

          {/* --- 4. Submissions List ----------------------------------------- */}
          {visible.length === 0 ? (
            <div className="card rounded-[16px] border border-line bg-surface p-8 shadow-2xs">
              <EmptyState
                icon={<Users className="h-6 w-6" />}
                title="No students match the criteria"
                description="Try clearing your search query or switching the status filter to see the complete class roster."
                action={
                  search || filter !== 'ALL' ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setSearch('');
                        setFilter('ALL');
                      }}
                    >
                      Reset filters
                    </Button>
                  ) : undefined
                }
              />
            </div>
          ) : (
            <ul className="flex flex-col gap-3">
              {visible.map((student) => (
                <li key={student.uuid}>
                  <SubmissionRow
                    homeworkUuid={data.homework.uuid}
                    maxMarks={data.homework.maxMarks}
                    student={student}
                    onSaved={refetch}
                  />
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

interface SetMaxMarksProps {
  homeworkUuid: string;
  onSaved: () => void;
}

/**
 * Gives unscored work a mark ceiling, so it can be marked.
 *
 * Here and not only in the edit form because the edit form closes at the due
 * date, which is exactly when a teacher sits down to mark. The server allows
 * this one change on overdue work and nothing else.
 */
function SetMaxMarks({ homeworkUuid, onSaved }: SetMaxMarksProps) {
  const [value, setValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const parsed = Number(value);
  const isValid = value.trim() !== '' && Number.isInteger(parsed) && parsed >= 1 && parsed <= 1000;

  async function save() {
    setIsSaving(true);

    try {
      await homeworkApi.update(homeworkUuid, { maxMarks: parsed });
      toast.success(`Marked out of ${parsed}`);
      onSaved();
    } catch (caught) {
      toast.error(
        'Could not set the marks',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form
      className="flex flex-col gap-2 border-t border-line pt-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (isValid) void save();
      }}
    >
      <p className="text-sm text-ink-2">
        This homework has no marks yet. Set what it is out of to start marking.
      </p>
      <div className="flex items-end gap-2">
        <div className="w-40">
          <Input
            label="Out of"
            type="number"
            min={1}
            max={1000}
            inputMode="numeric"
            placeholder="e.g. 20"
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
        </div>
        <Button type="submit" disabled={!isValid || isSaving}>
          {isSaving ? 'Saving…' : 'Set marks'}
        </Button>
      </div>
    </form>
  );
}

interface SubmissionRowProps {
  homeworkUuid: string;
  maxMarks: number | null;
  student: HomeworkStudentRow;
  onSaved: () => void;
}

/**
 * One student's submission row: status, evaluation form, attachment preview.
 */
function SubmissionRow({ homeworkUuid, maxMarks, student, onSaved }: SubmissionRowProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [marks, setMarks] = useState(student.marksObtained?.toString() ?? '');
  const [feedback, setFeedback] = useState(student.feedback ?? '');
  const [isSaving, setIsSaving] = useState(false);
  /** The handed-in file open in the preview, which has its own Download. */
  const [isPreviewing, setIsPreviewing] = useState(false);

  const state = stateOf(student);

  useEffect(() => {
    setMarks(student.marksObtained?.toString() ?? '');
    setFeedback(student.feedback ?? '');
  }, [student.marksObtained, student.feedback]);

  const isDirty =
    marks !== (student.marksObtained?.toString() ?? '') ||
    feedback !== (student.feedback ?? '');

  async function save() {
    setIsSaving(true);

    try {
      await homeworkApi.grade(homeworkUuid, student.uuid, {
        marksObtained: marks.trim() === '' ? null : Number(marks),
        feedback: feedback.trim() === '' ? null : feedback.trim(),
      });

      toast.success(`Saved marks for ${student.fullName}`);
      if (marks.trim() !== '') setIsOpen(false);
      onSaved();
    } catch (caught) {
      toast.error(
        `Could not save ${student.fullName}'s mark`,
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  const style = STATE_STYLE[state];
  const [first = '', ...rest] = student.fullName.split(' ').filter(Boolean);

  return (
    <div
      className={cn(
        'card flex flex-col gap-3.5 rounded-[14px] p-4 sm:p-4.5 border transition-all duration-200 shadow-2xs',
        style.card,
      )}
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        {/* Student Profile Info */}
        <div className="flex items-center gap-3 min-w-0 sm:w-60 sm:shrink-0">
          <span
            className={cn(
              'grid h-9 w-9 shrink-0 place-items-center rounded-full text-xs font-bold shadow-2xs border',
              getAvatarColor(student.uuid),
              style.avatarBorder,
            )}
            aria-hidden
          >
            {getInitials(first, rest.at(-1) ?? first)}
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-bold text-ink" title={student.fullName}>
              {student.fullName}
            </p>
            <p className="truncate font-mono text-2xs font-semibold text-ink-3">
              {student.rollNumber ? `Roll ${student.rollNumber} · ` : ''}
              {student.admissionNumber}
            </p>
          </div>
        </div>

        {/* Middle: Attachment, Repo link, and Feedback Chip */}
        <div className="flex flex-1 flex-wrap items-center gap-2 min-w-0 sm:px-3">
          {student.attachment && (
            <button
              type="button"
              onClick={() => setIsPreviewing(true)}
              title={`Preview ${student.attachment.fileName}`}
              aria-label={`Preview ${student.attachment.fileName}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface-2/80 px-2.5 py-1 text-xs font-mono text-ink-2 hover:border-accent hover:text-accent hover:bg-accent/5 transition-colors cursor-pointer"
            >
              <Paperclip className="h-3.5 w-3.5 text-accent shrink-0" />
              <span className="truncate max-w-[10rem] sm:max-w-[14rem]">
                {student.attachment.fileName}
              </span>
            </button>
          )}

          {student.repoUrl && (
            <a
              href={student.repoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface-2/80 px-2.5 py-1 text-xs font-mono text-ink-2 hover:border-accent hover:text-accent hover:bg-accent/5 transition-colors"
              title={student.repoUrl}
            >
              <Github className="h-3.5 w-3.5 text-ink-2 shrink-0" aria-hidden />
              <span className="truncate max-w-[10rem] sm:max-w-[14rem]">
                {student.repoUrl.replace(/^https?:\/\//, '')}
              </span>
            </a>
          )}

          {!isOpen && student.feedback && (
            <div
              className="inline-flex items-center gap-1.5 rounded-md bg-surface-2/70 border border-line/60 px-2.5 py-1 text-xs text-ink-2 max-w-full"
              title={student.feedback}
            >
              <MessageSquare className="h-3.5 w-3.5 text-accent shrink-0" />
              <span className="truncate max-w-[16rem] italic text-ink-2">"{student.feedback}"</span>
            </div>
          )}
        </div>

        {/* Evaluation Stats & Controls */}
        <div className="flex items-center gap-3 shrink-0 self-end sm:self-center">
          {/* Prominent score pill when marked */}
          {state === 'GRADED' && (
            <span className="inline-flex items-baseline gap-0.5 rounded-full bg-ok/10 border border-ok/25 px-2.5 py-0.5 text-sm font-bold text-ok">
              {student.marksObtained}
              {maxMarks !== null && (
                <span className="text-2xs font-semibold text-ink-3">/{maxMarks}</span>
              )}
            </span>
          )}

          <div className="flex flex-col items-end">
            <Badge tone={style.tone}>{style.label}</Badge>
            {student.submittedAt && (
              <span className="text-2xs font-medium text-ink-3 mt-0.5">
                {formatInstituteDateTime(student.submittedAt)}
              </span>
            )}
          </div>

          {/* Edit marks button */}
          {!isOpen && (
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Grade or edit marks for ${student.fullName}`}
              title="Record or update marks"
              leftIcon={<Pencil className="h-3.5 w-3.5" />}
              onClick={() => setIsOpen(true)}
              className="h-8 w-8 p-0 rounded-lg text-ink-3 hover:text-accent hover:bg-surface-2"
            />
          )}
        </div>
      </div>

      {/* Open state: inline grading form */}
      {isOpen && (
        <div className="border-t border-line pt-3.5 flex flex-col gap-3">
          {state === 'PENDING' && (
            <p className="text-xs text-ink-2 bg-surface-2/50 rounded-lg p-2.5 border border-line/60">
              No online submission received. You can directly record marks and remarks for work handed in physically.
            </p>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[10rem_minmax(0,1fr)]">
            <Input
              label={maxMarks === null ? 'Marks' : `Score (out of ${maxMarks})`}
              type="number"
              min={0}
              max={maxMarks ?? undefined}
              inputMode="numeric"
              placeholder={maxMarks === null ? 'Set "Out of" first' : `0 - ${maxMarks}`}
              disabled={maxMarks === null}
              hint={maxMarks === null ? 'Enter "Out of" at the top of the page and press Set marks first.' : undefined}
              value={marks}
              onChange={(event) => setMarks(event.target.value)}
              className="h-9.5 text-sm"
            />

            <Textarea
              label="Teacher remarks & feedback"
              rows={2}
              maxLength={1000}
              placeholder="Provide constructive feedback for the student..."
              value={feedback}
              onChange={(event) => setFeedback(event.target.value)}
              className="text-sm"
            />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <p className="text-xs font-semibold text-ink-3 ">
              {student.gradedBy ? `Last evaluated by ${student.gradedBy}` : 'Unassessed'}
            </p>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={isSaving}
                onClick={() => {
                  setMarks(student.marksObtained?.toString() ?? '');
                  setFeedback(student.feedback ?? '');
                  setIsOpen(false);
                }}
              >
                Cancel
              </Button>

              <Button
                variant="primary"
                size="sm"
                leftIcon={
                  isDirty ? <FileCheck className="h-4 w-4" /> : <Check className="h-4 w-4" />
                }
                isLoading={isSaving}
                disabled={!isDirty}
                onClick={() => void save()}
              >
                {isDirty ? 'Save Marks' : 'Saved'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {student.attachment && (
        <FilePreviewModal
          source={
            isPreviewing
              ? { path: homeworkApi.submissionFilePath(homeworkUuid, student.uuid) }
              : null
          }
          fileName={student.attachment.fileName}
          onClose={() => setIsPreviewing(false)}
        />
      )}
    </div>
  );
}
